import React, { useRef, useState } from 'react';
import { Box, IconButton, Stack } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ProductPhoto from './ProductPhoto';
import { prefersReducedMotion, useOutOfSight, useSlides } from './useSlides';

// How far a finger has to travel sideways before it counts as a swipe.
const SWIPE_PX = 40;

const FADE_MS = 600;

/**
 * The photos on a product card.
 *
 * They change as useSlides describes: the photos either side of the one
 * showing are loaded as soon as it is, so a change — by the arrows, a dot, a
 * swipe, or on its own — only fades an already decoded photo in over the
 * current one. Photos further away are left alone until they become
 * neighbours, so a card nobody looks at twice costs its cover and the next.
 *
 * With `autoPlaySeconds`, the photos also move on by themselves while the card
 * is in view. That waits while the cursor or keyboard focus is on the card, so
 * a photo never changes under someone looking at it, and stops for good on a
 * card someone has paged through themselves: they have taken over. A visitor
 * who has asked their device for less motion never sees it play.
 * `autoPlayDelayMs` holds back the first change, so the cards in a grid change
 * one after another rather than all at once.
 */
const ImageCarousel = ({
  photos = [],
  height = 250,
  alt = 'Product photo',
  sizes = '100vw',
  eager = false,
  autoPlaySeconds = null,
  autoPlayDelayMs = 0,
}) => {
  const count = photos.length;
  const frameRef = useRef(null);
  const touch = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [takenOver, setTakenOver] = useState(false);
  const [reducedMotion] = useState(prefersReducedMotion);
  // Most of the card has to be showing: a sliver at the bottom of the screen
  // changing photo is a distraction, not a preview.
  const outOfSight = useOutOfSight(frameRef, 0.5);

  const { index, mounted, go, markReady, layer } = useSlides({
    keys: photos.map((photo) => photo.key),
    playing:
      Boolean(autoPlaySeconds) && !reducedMotion && !takenOver && !hovered && !focused && !outOfSight,
    seconds: autoPlaySeconds || 0,
    firstDelayMs: autoPlayDelayMs,
    preload: 'both',
  });

  /** A change the visitor asked for, which ends playing on this card. */
  const goByHand = (target) => {
    setTakenOver(true);
    go(target);
  };

  const handleTouchStart = (event) => {
    const point = event.touches[0];
    touch.current = { x: point.clientX, y: point.clientY };
  };

  const handleTouchEnd = (event) => {
    const start = touch.current;
    touch.current = null;
    if (!start) return;
    const point = event.changedTouches[0];
    const dx = point.clientX - start.x;
    const dy = point.clientY - start.y;
    // Mostly sideways, so a scroll down the page is never taken for a swipe.
    if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
      goByHand(index + (dx < 0 ? 1 : -1));
    }
  };

  // A single photograph has nothing to page through, so it gets no controls.
  const hasMultiple = count > 1;

  const arrowSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 3,
    width: 32,
    height: 32,
    color: '#fff',
    backgroundColor: 'rgba(20, 22, 15, 0.45)',
    backdropFilter: 'blur(4px)',
    opacity: { xs: 1, md: 0 },
    transition: 'opacity .2s ease, background-color .2s ease',
    '&:hover': { backgroundColor: 'rgba(20, 22, 15, 0.72)' },
    '&:focus-visible': { opacity: 1 },
  };

  return (
    <Box
      ref={frameRef}
      onTouchStart={hasMultiple ? handleTouchStart : undefined}
      onTouchEnd={hasMultiple ? handleTouchEnd : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
      sx={{
        position: 'relative',
        height,
        overflow: 'hidden',
        backgroundColor: 'action.hover',
        touchAction: 'pan-y',
        // The arrows stay out of the way until the card is under the cursor.
        // Touch screens have no hover, so there they are simply always there.
        '&:hover .carousel-arrow': { opacity: 1 },
        '&:hover .carousel-track': { transform: 'scale(1.04)' },
      }}
    >
      <Box
        className="carousel-track"
        sx={{ position: 'absolute', inset: 0, transition: 'transform .5s ease' }}
      >
        {count === 0 ? (
          <ProductPhoto photo={null} alt={alt} sx={{ position: 'absolute', inset: 0 }} />
        ) : (
          photos.map((photo, i) =>
            mounted.has(i) ? (
              <Box
                key={photo.key}
                aria-hidden={i !== index}
                sx={{ position: 'absolute', inset: 0, ...layer(i, FADE_MS) }}
              >
                <ProductPhoto
                  photo={photo}
                  alt={i === index ? alt : ''}
                  sizes={sizes}
                  // The cover waits its turn like any lazy image; a neighbour
                  // is only in the page because it is wanted next, so it is
                  // fetched at once, behind whatever else is loading.
                  eager={eager || i !== 0}
                  priority={i === 0 ? (eager ? 'high' : undefined) : 'low'}
                  onReady={() => markReady(i)}
                  sx={{ width: '100%', height: '100%' }}
                />
              </Box>
            ) : null
          )
        )}
      </Box>

      {hasMultiple && (
        <>
          <IconButton
            className="carousel-arrow"
            onClick={() => goByHand(index - 1)}
            aria-label="Previous photo"
            sx={{ ...arrowSx, left: 8 }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton
            className="carousel-arrow"
            onClick={() => goByHand(index + 1)}
            aria-label="Next photo"
            sx={{ ...arrowSx, right: 8 }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 15 }} />
          </IconButton>

          <Stack
            direction="row"
            spacing={0.75}
            justifyContent="center"
            sx={{ position: 'absolute', left: 0, right: 0, bottom: 10, zIndex: 3 }}
          >
            {photos.map((photo, i) => (
              <Box
                key={photo.key}
                component="button"
                type="button"
                onClick={() => goByHand(i)}
                aria-label={`Show photo ${i + 1}`}
                aria-current={i === index ? 'true' : undefined}
                sx={{
                  p: 0,
                  border: 0,
                  width: i === index ? 18 : 6,
                  height: 6,
                  borderRadius: 999,
                  cursor: 'pointer',
                  transition: 'width .25s ease, background-color .25s ease',
                  backgroundColor:
                    i === index ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)',
                }}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default ImageCarousel;
